import React, { useEffect, useState, useRef } from "react";
import { GoogleMap } from "@react-google-maps/api";
import { useGoogleMaps } from "../../config/google-maps-loader";
import { useSocket } from "../../context/socket-context";
import {
  Loader2,
  Star,
  MapPin,
  Phone,
  Mail,
  X,
  Plus,
  Minus,
  Search as SearchIcon,
  User,
  Timer,
  AlertCircle,
} from "lucide-react";
import useCountdown from "../../hooks/use-countdown";
import tokenService from "../../services/token-service";
import apiUrls from "../../config/api-urls";
import axios from "axios";

const CountdownDisplay = ({ expiryTime }) => {
  const { minutes, seconds } = useCountdown(expiryTime);
  return <>{String(minutes).padStart(2, '0')}:{String(seconds).padStart(2, '0')}</>;
};

const SearchingHandymen = ({ task, onOfferSelected }) => {
  const [handymen, setHandymen] = useState([]);
  const [offers, setOffers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedHandyman, setSelectedHandyman] = useState(null);
  const [customPrice, setCustomPrice] = useState(null);
  const [totalAmount, setTotalAmount] = useState(0);
  const [expiredNotifications, setExpiredNotifications] = useState([]);
  const [showNoProvidersAlert, setShowNoProvidersAlert] = useState(false);

  const token = tokenService.getToken();
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const { isLoaded } = useGoogleMaps();
  const {
    socket,
    expiredRequests,
    expiredOffers,
    setExpiredRequests,
    setExpiredOffers,
    noProvidersFound,
    setNoProvidersFound,
    counterOffers
  } = useSocket();

  useEffect(() => {
    if (typeof customPrice === "number") {
      setTotalAmount(customPrice);
    }
  }, [customPrice]);

  // read selectedCategory from localStorage & compute total
  useEffect(() => {
    const stored = localStorage.getItem("selectedCategory");
    let cat = null;
    if (stored) {
      try {
        cat = JSON.parse(stored);
        setSelectedCategoryLocal(cat);
      } catch (e) {
        console.warn("Failed parse selectedCategory from localStorage", e);
      }
    }

    // compute total
    if (cat?.cartItems) {
      const total = cat.cartItems.reduce((s, i) => s + (i.customPrice || 0), 0);
      setTotalAmount(total);
      setCustomPrice(total);
    } else {
      setTotalAmount(0);
      setCustomPrice(0);
    }
  }, []);

  // keep a reference local state for selectedCategory inside component
  const [selectedCategoryLocal, setSelectedCategoryLocal] = useState(null);

  // Listen for counter offers and update existing offers
  useEffect(() => {
    if (!task?._id || !socket) return;

    const handleCounterOffer = (counterOfferData) => {
      console.log('💰 ===== COUNTER OFFER RECEIVED =====');
      console.log('📋 Counter Offer Data:', JSON.stringify(counterOfferData, null, 2));

      // Normalize counter offer data
      const normalizedCounterOffer = {
        ...counterOfferData,
        handymanId: counterOfferData.handymanId || counterOfferData.handyman?.id || counterOfferData.handyman?._id,
        isCounterOffer: true
      };

      console.log('⏰ Counter Offer Expiry Time:', normalizedCounterOffer.expiryTime);

      // Update offers state - either update existing or add new
      setOffers(prev => {
        // Check if this is an update to an existing counter offer
        const existingOfferIndex = prev.findIndex(o => 
          o.offerId === normalizedCounterOffer.offerId || 
          (o.serviceRequestId === normalizedCounterOffer.serviceRequestId && 
           o.handymanId === normalizedCounterOffer.handymanId)
        );

        if (existingOfferIndex !== -1) {
          // Update existing offer
          console.log('🔄 Updating existing counter offer with new price:', 
            normalizedCounterOffer.proposedPrice);
          
          const updatedOffers = [...prev];
          updatedOffers[existingOfferIndex] = {
            ...updatedOffers[existingOfferIndex],
            proposedPrice: normalizedCounterOffer.proposedPrice,
            priceChange: normalizedCounterOffer.priceChange,
            message: normalizedCounterOffer.message,
            expiryTime: new Date(Date.now() + 2 * 60 * 1000).toISOString(), // Reset timer to 2 minutes
            updatedAt: new Date().toISOString()
          };
          return updatedOffers;
        } else {
          // Add new counter offer
          console.log('➕ Adding new counter offer');
          return [...prev, {
            ...normalizedCounterOffer,
            expiryTime: new Date(Date.now() + 2 * 60 * 1000).toISOString(),
            isCounterOffer: true
          }];
        }
      });

      // Update handymen list if needed
      if (normalizedCounterOffer.handyman) {
        setHandymen(prev => {
          const exists = prev.find(h =>
            h._id === normalizedCounterOffer.handymanId ||
            h.user?._id === normalizedCounterOffer.handymanId
          );
          if (exists) return prev;

          const newHandyman = {
            _id: normalizedCounterOffer.handyman.id || normalizedCounterOffer.handyman._id,
            name: normalizedCounterOffer.handyman.fullName || normalizedCounterOffer.handyman.name,
            profileImage: normalizedCounterOffer.handyman.profileImage,
            rating: normalizedCounterOffer.handyman.rating,
            ratingCount: normalizedCounterOffer.handyman.ratingCount,
            location: normalizedCounterOffer.handyman.location,
            completedJobs: normalizedCounterOffer.handyman.completedJobs || 0,
            ...normalizedCounterOffer.handyman
          };
          return [...prev, newHandyman];
        });
      }
    };

    socket.on("counterOffer", handleCounterOffer);

    return () => {
      socket.off("counterOffer", handleCounterOffer);
    };
  }, [task?._id, socket]);

  // join socket room and listen for new offers
  useEffect(() => {
    if (!task?._id || !socket) return;

    socket.emit("joinTaskRoom", task._id);

    // Listen for new offers from handymen
    const handleNewOffer = (offerData) => {
      console.log('📋 Offer Data:', JSON.stringify(offerData, null, 2));

      // Normalize offerData to ensure we have handymanId
      const normalizedOffer = {
        ...offerData,
        handymanId: offerData.handymanId || offerData.handyman?.id || offerData.handyman?._id
      };
      // Add the new offer to the offers list with expiry time
      setOffers(prev => {
        // Avoid duplicates
        const exists = prev.find(o => o.offerId === normalizedOffer.offerId);
        if (exists) {
          return prev;
        }
        const updated = [...prev, {
          ...normalizedOffer,
          // Override backend expiry to 2 minutes (customer requirement)
          expiryTime: new Date(Date.now() + 2 * 60 * 1000).toISOString(),
          isCounterOffer: false
        }];
        return updated;
      });

      // Also add the handyman to the handymen list if not already present
      if (normalizedOffer.handyman) {
        setHandymen(prev => {
          const exists = prev.find(h =>
            h._id === normalizedOffer.handymanId ||
            h.user?._id === normalizedOffer.handymanId
          );
          if (exists) return prev;

          // Create a handyman object from the offer data
          const newHandyman = {
            _id: normalizedOffer.handyman.id || normalizedOffer.handyman._id,
            name: normalizedOffer.handyman.fullName || normalizedOffer.handyman.name,
            profileImage: normalizedOffer.handyman.profileImage,
            rating: normalizedOffer.handyman.rating,
            ratingCount: normalizedOffer.handyman.ratingCount,
            location: normalizedOffer.handyman.location,
            completedJobs: normalizedOffer.handyman.completedJobs || 0,
            ...normalizedOffer.handyman
          };
          return [...prev, newHandyman];
        });
      }
    };

    socket.on("newOffer", handleNewOffer);
    socket.on("nearbyHandyman", (data) => {
      console.log('📍 Nearby handyman:', data);
    });

    return () => {
      socket.off("nearbyHandyman");
      socket.off("newOffer", handleNewOffer);
    };
  }, [task?._id, socket]);

  // Listen for offer acceptance to remove offer from list
  useEffect(() => {
    if (!socket) return;

    const handleOfferAccepted = (data) => {
      // Remove the accepted offer from offers list
      if (data.offerId) {
        setOffers(prev => prev.filter(o => o.offerId !== data.offerId));
      }
    };

    socket.on('offer:accepted', handleOfferAccepted);

    return () => {
      socket.off('offer:accepted', handleOfferAccepted);
    };
  }, [socket]);

  // Listen for counter offers from SocketContext (backup)
  useEffect(() => {
    if (!task?._id) return;

    // Filter counter offers for this service request
    const relevantCounterOffers = counterOffers.filter(
      co => co.serviceRequestId === task._id
    );

    if (relevantCounterOffers.length > 0) {
      relevantCounterOffers.forEach((counterOffer) => {
        // Update or add counter offer to offers list
        setOffers(prev => {
          // Check if this offer already exists
          const existingIndex = prev.findIndex(o => o.offerId === counterOffer.offerId);
          
          if (existingIndex !== -1) {
            // Update existing offer
            const updatedOffers = [...prev];
            updatedOffers[existingIndex] = {
              ...updatedOffers[existingIndex],
              proposedPrice: counterOffer.proposedPrice,
              priceChange: counterOffer.priceChange,
              message: counterOffer.message,
              expiryTime: new Date(Date.now() + 2 * 60 * 1000).toISOString(),
              updatedAt: new Date().toISOString()
            };
            return updatedOffers;
          } else {
            // Add new counter offer
            return [...prev, {
              ...counterOffer,
              expiryTime: new Date(Date.now() + 2 * 60 * 1000).toISOString(),
              isCounterOffer: true
            }];
          }
        });
      });
    }
  }, [counterOffers, task?._id]);

  // Handle expired offers - remove from list and show notification
  useEffect(() => {
    // Check for expired offers every 1 second
    const intervalId = setInterval(() => {
      const now = new Date().getTime();

      setOffers(prevOffers => {
        const remainingOffers = [];
        const expired = [];

        prevOffers.forEach(offer => {
          if (offer.expiryTime && new Date(offer.expiryTime).getTime() < now) {
            expired.push(offer);
          } else {
            remainingOffers.push(offer);
          }
        });

        if (expired.length > 0) {
          // Add to notifications
          expired.forEach(exOffer => {
            setExpiredNotifications(prev => {
              // Avoid duplicate notifications
              if (prev.some(n => n.id === exOffer.offerId)) return prev;

              return [
                ...prev,
                {
                  id: exOffer.offerId,
                  message: `Offer from ${exOffer.handymanName || exOffer.handyman?.fullName || 'handyman'} has expired`,
                  timestamp: new Date()
                }
              ];
            });

            // Auto-remove notification after 5 seconds
            setTimeout(() => {
              setExpiredNotifications(prev => prev.filter(n => n.id !== exOffer.offerId));
            }, 5000);
          });
        }

        return remainingOffers.length !== prevOffers.length ? remainingOffers : prevOffers;
      });
    }, 1000);

    return () => clearInterval(intervalId);
  }, []); // Run on mount

  // Also listen to socket for explicit expiry events (backup)
  useEffect(() => {
    if (!task?._id) return;

    const relevantExpiredOffers = expiredOffers.filter(
      eo => eo.serviceRequestId === task._id
    );

    if (relevantExpiredOffers.length > 0) {
      setOffers(prev => prev.filter(o => !relevantExpiredOffers.some(reo => reo.offerId === o.offerId)));

      // Clear processed from context to avoid re-processing
      setExpiredOffers(prev =>
        prev.filter(eo => !relevantExpiredOffers.some(reo => reo.offerId === eo.offerId))
      );
    }
  }, [expiredOffers, task?._id, setExpiredOffers]);

  // Handle No Providers Found Event (from Socket)
  useEffect(() => {
    if (noProvidersFound && noProvidersFound.serviceRequestId === task?._id) {
      setShowNoProvidersAlert(true);
    }
  }, [noProvidersFound, task?._id]);

  // Handle expired requests notification
  useEffect(() => {
    if (!task?._id) return;

    const relevantExpiredRequests = expiredRequests.filter(
      er => er.serviceRequestId === task._id
    );

    relevantExpiredRequests.forEach((expiredRequest) => {
      // Show notification
      setExpiredNotifications(prev => [
        ...prev,
        {
          id: `request-${expiredRequest.serviceRequestId}`,
          message: expiredRequest.message || 'Your request has expired. Create a new one?',
          timestamp: new Date(),
          isRequest: true
        }
      ]);

      // Auto-remove notification after 10 seconds
      setTimeout(() => {
        setExpiredNotifications(prev =>
          prev.filter(n => n.id !== `request-${expiredRequest.serviceRequestId}`)
        );
      }, 10000);
    });

    // Clear processed expired requests
    if (relevantExpiredRequests.length > 0) {
      setExpiredRequests(prev =>
        prev.filter(er => !relevantExpiredRequests.some(rer => rer.serviceRequestId === er.serviceRequestId))
      );
    }
  }, [expiredRequests, task?._id, setExpiredRequests]);

  // markers: update map markers for customer + handymen
  useEffect(() => {
    if (!isLoaded || !mapRef.current) return;
    if (!window.google?.maps) return;
    const map = mapRef.current;
    const bounds = new window.google.maps.LatLngBounds();

    // clear existing
    markersRef.current.forEach((m) => (m.setMap ? m.setMap(null) : (m.map = null)));
    markersRef.current = [];

    // customer marker
    if (task?.location?.coordinates) {
      const customerPos = {
        lat: task.location.coordinates[1],
        lng: task.location.coordinates[0],
      };

      const cm = new window.google.maps.Marker({
        map,
        position: customerPos,
        title: "Customer Location",
        icon: "https://maps.google.com/mapfiles/ms/icons/purple-dot.png",
      });
      markersRef.current.push(cm);
      bounds.extend(customerPos);
    }

    // handyman markers
    console.log('🗺️ Updating Handyman Markers. Count:', handymen.length);
    handymen.forEach((h, index) => {
      console.log(`👤 Handyman [${index}] (${h.name}):`, h.location);

      // Handle potential location formats
      let lat, lng;

      if (h?.location?.coordinates && Array.isArray(h.location.coordinates)) {
        // GeoJSON format: [lng, lat]
        lat = h.location.coordinates[1];
        lng = h.location.coordinates[0];
      } else if (h?.location?.lat && h?.location?.lng) {
        // Object format: { lat, lng }
        lat = h.location.lat;
        lng = h.location.lng;
      }

      if (!lat || !lng) {
        console.warn(`⚠️ Skipped marker for ${h.name} - Invalid location data`, h.location);
        return;
      }

      const pos = { lat, lng };
      console.log(`📍 Placing marker at:`, pos);

      const hm = new window.google.maps.Marker({
        map,
        position: pos,
        title: h.name,
        icon: "https://maps.google.com/mapfiles/ms/icons/green-dot.png",
      });
      markersRef.current.push(hm);
      bounds.extend(pos);
    });

    // Fit bounds if we have markers
    if (markersRef.current.length > 0) {
      map.fitBounds(bounds);
      // Optional: Add some padding by zooming out slightly if needed, or rely on default padding
      // Check if only one marker (customer), then we might want to keep the default zoom
      if (markersRef.current.length === 1) {
        map.setZoom(14);
      }
    }
  }, [isLoaded, handymen, task]);

  // helper: call backend search-handymen endpoint
  const findHandymen = async () => {
    if (!selectedCategoryLocal) {
      setHandymen([]);
      setOffers([]);
      return;
    }
    setIsSearching(true);
    setOffers([]);
    setHandymen([]);

    try {
      const formData = new FormData();
      formData.append("mainServiceId", selectedCategoryLocal.cartItems[0].mainService.id);
      formData.append(
        "subServiceIds",
        JSON.stringify(selectedCategoryLocal.cartItems.map((item) => item.id))
      );
      const finalDescription = selectedCategoryLocal.cartItems.map((i) => i.description).join(" | ");
      formData.append("description", finalDescription || "");
      formData.append("location",
        JSON.stringify({
          type: "Point",
          coordinates: task.location.coordinates,
          address: task.location.address || "",
        })
      );
      const image = selectedCategoryLocal.cartItems[0]?.image;
      const video = selectedCategoryLocal.cartItems[0]?.video;

      formData.append("image", image);
      formData.append("video", video);


      const basePriceToSend = typeof customPrice === "number" ? customPrice : totalAmount;
      if (basePriceToSend) formData.append("basePrice", basePriceToSend);

      const endpoint = apiUrls.services.customerServiceRequest;
      const res = await axios.post(endpoint, formData, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "multipart/form-data" },
      });

      // try to extract data flexibly
      const responseData = res?.data || {};
      // if backend returns `handymen` list in data
      const returnedHandymen = responseData.data?.handymen || responseData.handymen || responseData.data || [];
      const returnedOffers = responseData.data?.offers || responseData.offers || [];

      if (Array.isArray(returnedHandymen) && returnedHandymen.length > 0) {
        setHandymen(returnedHandymen);
      } else {
        setHandymen([]);
        // If no handymen found immediately via API, show alert
        if (!returnedOffers || returnedOffers.length === 0) {
          setShowNoProvidersAlert(true);
        }
      }

      if (Array.isArray(returnedOffers) && returnedOffers.length > 0) {
        setOffers(returnedOffers);
      } else {
        setOffers([]);
      }
    } catch (err) {
      console.error("Find handymen API failed:", err);
      setHandymen([]);
      setOffers([]);
    } finally {
      setIsSearching(false);
    }
  };

  // UI helpers for custom price
  const increasePrice = () => {
    setCustomPrice((p) => (typeof p === "number" ? Math.round((p + 50) * 100) / 100 : totalAmount || 0));
  };

  const decreasePrice = () => {
    setCustomPrice((p) => {
      if (typeof p !== "number") return totalAmount || 0;
      const next = p - 50;
      return next >= 0 ? Math.round(next * 100) / 100 : 0;
    });
  };

  const handleOfferSelect = async (offer) => {
    try {
      const handyman = handymen.find((h) => h._id === offer.handymanId || h.user?._id === offer.handymanId);
      // Call the accept offer API
      const response = await axios.post(
        apiUrls.services.selectHandymanOffer,
        {
          offerId: offer.offerId,
          serviceRequestId: task._id,
          handymanId: offer.handymanId,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      localStorage.removeItem("serviceType");
      localStorage.removeItem("selectedCategory");
      localStorage.removeItem("activeTask");


      // Navigate to tracking component
      onOfferSelected({
        serviceRequestId: task._id,
        handyman,
        task,
        offer,
      });
    } catch (error) {
      console.error("Error accepting offer:", error);
    }
  };

  const handleViewProfile = (handymanId) => {
    const handyman = handymen.find((h) => h._id === handymanId || h.user?._id === handymanId);
    setSelectedHandyman(handyman);
  };

  const closeProfileModal = () => setSelectedHandyman(null);

  if (!isLoaded) return <div>Loading map...</div>;

  return (
    <div className="min-h-screen w-full relative">
      {/* Expiry Notifications */}
      {expiredNotifications.length > 0 && (
        <div className="fixed top-4 right-4 z-50 space-y-2">
          {expiredNotifications.map((notification) => (
            <div
              key={notification.id}
              className="bg-red-500 text-white px-6 py-3 rounded-lg shadow-2xl flex items-center gap-3 animate-slideInRight max-w-md"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-semibold">{notification.message}</p>
              </div>
              <button
                onClick={() => setExpiredNotifications(prev => prev.filter(n => n.id !== notification.id))}
                className="text-white hover:text-red-100 transition-colors"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Map - Full Screen */}
      <GoogleMap
        onLoad={(map) => (mapRef.current = map)}
        center={{
          lat: task.location.coordinates[1],
          lng: task.location.coordinates[0],
        }}
        zoom={14}
        mapContainerStyle={{ width: "100%", height: "100vh" }}
      />

      {/* Vertical Handyman Cards on Left Side */}
      {offers.length > 0 && (
        <div className="absolute left-4 w-80 z-40 flex flex-col gap-3 max-h-[calc(100vh-160px)] overflow-y-auto no-scrollbar pb-4 top-20">
          {offers.map((offer) => {
            const handyman =
              handymen.find((h) => h._id === offer.handymanId) ||
              handymen.find((h) => h.user?._id === offer.handymanId);
            if (!handyman) return null;

            return (
              <div
                key={offer.offerId}
                className="bg-white dark:bg-slate-800 rounded-2xl shadow-lg border border-gray-200 dark:border-slate-700 p-4 hover:shadow-2xl hover:scale-[1.02] transition-all duration-300 animate-slideInLeft"
              >
                {/* Handyman Info */}
                <div className="flex items-start gap-4 mb-3">
                  <div className="relative flex-shrink-0">
                    <img
                      src={handyman.profileImage || handyman.user?.profileImage}
                      className="w-14 h-14 rounded-full object-cover border-2 border-blue-200 dark:border-blue-700 shadow-md ring-2 ring-blue-100 dark:ring-blue-900"
                      alt="profile"
                    />
                    <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-green-500 border-2 border-white dark:border-slate-800"></div>
                  </div>

                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <div className="flex flex-col gap-1 flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h4 className="font-bold text-gray-900 dark:text-white text-base truncate">
                            {handyman.name || handyman.user?.fullName}
                          </h4>
                          {/* Show Counter badge */}
                          {offer.isCounterOffer && offer.originalPrice && offer.proposedPrice !== offer.originalPrice && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-purple-100 to-pink-100 text-purple-700 border border-purple-300 dark:from-purple-900/30 dark:to-pink-900/30 dark:text-purple-300 dark:border-purple-700 flex-shrink-0">
                              Counter
                            </span>
                          )}
                          {/* Show Your Price tag */}
                          {(!offer.isCounterOffer || (offer.originalPrice && offer.proposedPrice === offer.originalPrice)) && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-gradient-to-r from-green-100 to-emerald-100 text-green-700 border border-green-300 dark:from-green-900/30 dark:to-emerald-900/30 dark:text-green-300 dark:border-green-700 flex-shrink-0">
                              Your Price
                            </span>
                          )}
                        </div>

                        <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
                          <span className="flex items-center gap-1">
                            <User className="w-3 h-3" />
                            {handyman.completedJobs || 0} jobs
                          </span>
                          {offer.handyman?.distanceInKm !== undefined && (
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3 text-purple-500" />
                              {offer.handyman.distanceInKm.toFixed(1)} km
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end gap-1.5">
                        {/* Rating */}
                        <div className="flex items-center gap-1 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/30 dark:to-yellow-900/30 px-2 py-1 rounded-full border border-amber-200 dark:border-amber-800">
                          <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                          <span className="text-xs font-bold text-amber-700 dark:text-amber-400">
                            {handyman.rating ?? handyman.rating?.toFixed(1)}
                          </span>
                        </div>

                        {/* Expiry Timer */}
                        <div className="flex flex-col items-end mt-1">
                          <div className="flex items-center gap-1 text-[10px] font-bold text-red-500 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded border border-red-100 dark:border-red-900/50">
                            <Timer className="w-3 h-3" />
                            <span className="font-mono">
                              <CountdownDisplay expiryTime={offer.expiryTime} />
                            </span>
                          </div>
                          <span className="text-[9px] text-gray-400 font-bold uppercase tracking-wider scale-90 origin-right">Expires in</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Counter Offer Price - Show in center ONLY if handyman proposed different price */}
                {offer.isCounterOffer && offer.originalPrice && offer.proposedPrice !== offer.originalPrice && (
                  <div className="mb-3 p-4 bg-gradient-to-r from-purple-50 via-pink-50 to-purple-50 dark:from-purple-900/20 dark:via-pink-900/20 dark:to-purple-900/20 rounded-xl border-2 border-purple-300 dark:border-purple-700 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <span className="text-sm line-through text-gray-500 dark:text-gray-400">Rs {offer.originalPrice}</span>
                      <span className="text-2xl font-bold text-purple-700 dark:text-purple-300">Rs {offer.proposedPrice}</span>
                    </div>
                    <p className="text-xs text-purple-600 dark:text-purple-400 font-semibold">💰 Counter Offer</p>
                  </div>
                )}
                {/* Accepted offer - show price prominently */}
                {(!offer.isCounterOffer || (offer.originalPrice && offer.proposedPrice === offer.originalPrice)) && (
                  <div className="mb-3 p-4 bg-gradient-to-r from-green-50 via-emerald-50 to-green-50 dark:from-green-900/20 dark:via-emerald-900/20 dark:to-green-900/20 rounded-xl border-2 border-green-300 dark:border-green-700 text-center">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <span className="text-2xl font-bold text-green-700 dark:text-green-300">Rs {offer.proposedPrice}</span>
                    </div>
                    <p className="text-xs text-green-600 dark:text-green-400 font-semibold">💰 Your Price</p>
                  </div>
                )}

                {/* Action Buttons: Decline (Left) - Profile (Center) - Accept (Right) */}
                <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-gray-200 dark:border-slate-700">
                  {/* View Profile */}
                  <button
                    onClick={() => handleViewProfile(handyman._id || handyman.user?._id)}
                    className="flex-1 px-3 py-2.5 rounded-xl border-2 border-gray-300 dark:border-slate-600 text-xs font-semibold text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-slate-700 transition-all"
                  >
                    Profile
                  </button>

                  {/* Accept */}
                  <button
                    onClick={() => handleOfferSelect(offer)}
                    className="flex-1 px-3 py-2.5 rounded-xl bg-gradient-to-r from-green-600 to-emerald-600 text-white text-xs font-bold hover:from-green-700 hover:to-emerald-700 transition-all shadow-lg hover:shadow-xl transform hover:scale-105"
                  >
                    Accept
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Compact Price Input at Bottom */}
      <div className="absolute bottom-6 left-1/2 transform -translate-x-1/2 z-40 w-full max-w-2xl px-4">
        <div className="bg-white/90 backdrop-blur-md rounded-full shadow-2xl border border-gray-200 p-2 pl-4 flex items-center justify-between gap-3">

          <div className="flex flex-col min-w-[160px] max-w-[200px]">
            {selectedCategoryLocal?.categoryName && (
              <span className="text-xs font-bold text-gray-900 whitespace-nowrap overflow-hidden text-ellipsis mb-0.5" title={selectedCategoryLocal.categoryName}>
                {selectedCategoryLocal.categoryName}
              </span>
            )}
            <div className="flex items-baseline gap-1">
              <span className="text-[10px] text-gray-500 font-semibold uppercase tracking-wider">Total</span>
              <span className="text-sm font-bold text-blue-600">Rs {totalAmount}</span>
            </div>
          </div>

          <div className="h-8 w-[1px] bg-gray-200"></div>

          <div className="flex items-center gap-2 bg-gray-50 rounded-full p-1 border border-gray-100">
            <button
              onClick={decreasePrice}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-white shadow-sm text-gray-600 hover:text-blue-600 transition"
            >
              <Minus className="w-3.5 h-3.5" />
            </button>

            <input
              type="number"
              value={typeof customPrice === "number" ? customPrice : ""}
              onChange={(e) => setCustomPrice(Number(e.target.value || 0))}
              className="w-24 bg-transparent text-center text-sm font-bold text-gray-800 focus:outline-none"
              placeholder="Price"
            />

            <button
              onClick={increasePrice}
              className="w-7 h-7 flex items-center justify-center rounded-full bg-white shadow-sm text-gray-600 hover:text-blue-600 transition"
            >
              <Plus className="w-3.5 h-3.5" />
            </button>
          </div>

          <button
            onClick={() => findHandymen()}
            disabled={isSearching}
            className="relative px-8 h-10 flex items-center justify-center bg-gradient-to-r from-blue-600 to-blue-500 text-white rounded-full transition-all shadow-lg hover:shadow-xl disabled:opacity-50 gap-2 text-sm font-bold tracking-wide overflow-hidden group"
          >
            {/* Animated shimmer effect */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>

            {isSearching ? (
              <Loader2 className="w-4 h-4 animate-spin relative z-10" />
            ) : (
              <>
                <SearchIcon className="w-4 h-4 relative z-10 group-hover:scale-110 transition-transform" />
                <span className="relative z-10">Find Offers</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Searching overlay */}
      {
        isSearching && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/30 z-30 text-white">
            <Loader2 className="w-8 h-8 animate-spin mb-2" />
            <div className="text-lg font-semibold">Searching nearby handymen...</div>
          </div>
        )
      }

      {/* Profile modal */}
      {
        selectedHandyman && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 animate-fadeIn">
            <div className="bg-white rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold text-gray-800">Handyman Profile</h3>
                <button
                  onClick={closeProfileModal}
                  className="text-gray-400 hover:text-gray-600 transition p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="p-4">
                <div className="flex items-center gap-4 mb-6">
                  <img
                    src={selectedHandyman.profileImage || selectedHandyman.user?.profileImage}
                    alt={selectedHandyman.name || selectedHandyman.user?.fullName}
                    className="w-20 h-20 rounded-full object-cover border-4 border-blue-100"
                  />
                  <div className="flex-1">
                    <h2 className="text-xl font-bold text-gray-800">
                      {selectedHandyman.name || selectedHandyman.user?.fullName}
                    </h2>
                    <div className="flex items-center gap-2 mt-1">
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
                        <span className="font-semibold text-amber-700">
                          {selectedHandyman.rating ?? selectedHandyman.rating?.toFixed(1)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="space-y-3 mb-6">
                  <h4 className="font-semibold text-gray-700 border-b pb-1">Contact Information</h4>
                  <div className="flex items-center gap-3 text-sm">
                    <Phone className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700">{selectedHandyman.phone || selectedHandyman.user?.phoneNumber}</span>
                  </div>
                  <div className="flex items-center gap-3 text-sm">
                    <Mail className="w-4 h-4 text-gray-500" />
                    <span className="text-gray-700">{selectedHandyman.email || selectedHandyman.user?.email}</span>
                  </div>
                </div>

                <div className="space-y-4 mb-6">
                  <h4 className="font-semibold text-gray-700 border-b pb-1">Professional Details</h4>
                  <div className="grid grid-cols-2 gap-4 text-sm">

                    <div>
                      <span className="text-gray-500">Completed Jobs</span>
                      <p className="font-medium text-gray-800">{selectedHandyman.completedJobs ?? "—"}</p>
                    </div>

                    <div>
                      <span className="text-gray-500">Rating</span>
                      <p className="font-medium text-gray-800">{selectedHandyman.rating}/5</p>
                    </div>
                  </div>
                </div>

                {selectedHandyman.skills && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 border-b pb-1 mb-3">Skills & Specializations</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedHandyman.skills.map((skill, idx) => (
                        <span key={idx} className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {selectedHandyman.about && (
                  <div className="mb-6">
                    <h4 className="font-semibold text-gray-700 border-b pb-1 mb-3">About</h4>
                    <p className="text-sm text-gray-700 leading-relaxed">{selectedHandyman.about}</p>
                  </div>
                )}

                <div className="flex gap-2 pt-4 border-t">
                  <button onClick={closeProfileModal} className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg hover:bg-gray-300 transition font-semibold">
                    Close Profile
                  </button>
                </div>
              </div>
            </div>
          </div>
        )
      }
    </div>
  );
};

export default SearchingHandymen;